import { getRefreshToken, setAuthToken } from '@/helpers'
import { history, ROUTES_AUTH } from '@/router'
import * as Sentry from '@sentry/react'
import axios from 'axios'

import { SENTRY_EXCLUDED_ENDPOINTS } from './api.constants'
import { AUTH_SIGN_IN_REFRESH_URL } from './api.urls'

export const api = axios.create({
  timeout: 60000,
})

export const apiNoAuth = axios.create({
  timeout: 60000,
})

let isRefreshing = false
let refreshSubscribers: ((value: unknown) => void)[] = []

const isSentryEndpointExcluded = (url: string, method: string): boolean => {
  if (!url || !method) return false

  const urlWithoutQuery = new URL(url).pathname
  const methodLower = method.toLowerCase()

  const excludedEndpoints = SENTRY_EXCLUDED_ENDPOINTS || []

  return excludedEndpoints.some(endpoint => {
    if (endpoint.method.toLowerCase() !== methodLower) {
      return false
    }

    if (typeof endpoint.pathname === 'string') {
      return urlWithoutQuery.includes(endpoint.pathname)
    } else if (endpoint.pathname instanceof RegExp) {
      return endpoint.pathname.test(urlWithoutQuery)
    }

    return false
  })
}

function subscribeToRefresh(cb: (value: unknown) => void): void {
  refreshSubscribers.push(cb)
}

function onRefreshed(result: string | Error) {
  refreshSubscribers.forEach((cb) => cb(result))
  refreshSubscribers = []
}

export const refreshToken = async () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      subscribeToRefresh((result) => {
        if (result instanceof Error) {
          reject(result)
        } else {
          resolve(result)
        }
      })
    })
  }

  isRefreshing = true
  let result

  try {
    const refresh = getRefreshToken()

    if (!refresh) {
      throw new Error('No refresh token available')
    }

    const res = await apiNoAuth.post(AUTH_SIGN_IN_REFRESH_URL, { refresh })

    if (res.data.access) {
      result = res.data.access
      setAuthToken(result)
      return result
    } else {
      throw new Error('Failed to refresh access token')
    }
  } catch (error) {
    result = error
    throw error
  } finally {
    isRefreshing = false
    onRefreshed(result)
  }
}

api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config
    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      try {
        const res = await refreshToken()

        api.defaults.headers.common.Authorization = `Bearer ${res}`
        originalRequest.headers.Authorization = `Bearer ${res}`
        setAuthToken(res)

        return api(originalRequest)
      } catch (e) {
        console.error({ e })

        if (error.response.status === 401 && originalRequest._retry) {
          history.replace(ROUTES_AUTH.SIGN_IN.fullPath())
        }

        return Promise.reject(e)
      }
    } else {
      Sentry.withScope((scope: any) => {
        if (error?.response && !isSentryEndpointExcluded(error?.config?.url || '', error?.config?.method || '')) {
          scope.setExtra('response', {
            data: JSON.stringify(error?.response?.data),
            status: error?.response?.status,
            headers: JSON.stringify(error?.response?.headers),
          })
        }

        scope.setExtra('config', {
          url: error?.config?.url,
          method: error?.config?.method,
          data: JSON.stringify(error?.config?.data),
          headers: JSON.stringify(error?.config?.headers),
        })
        Sentry.captureException(error)
      })

      return Promise.reject(error)
    }
  },
)
